import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { AdminContractRow } from '../api/client';
import { useAdminAuth } from '../auth/AuthProvider';
import { StatusBadge } from '../components/StatusBadge';
import { formatDate } from '../lib/format';

const STATUSES = [
  '',
  'draft',
  'sent',
  'viewed',
  'signed',
  'declined',
  'void',
  'completed',
];

export function ContractsPage() {
  const { fetcher } = useAdminAuth();
  const [status, setStatus] = useState('');
  const [accountId, setAccountId] = useState('');
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'contracts', status, accountId],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('page_size', '200');
      if (status) params.set('status', status);
      if (accountId) params.set('account_id', accountId);
      return fetcher<{ contracts: AdminContractRow[] }>(
        `/admin/api/contracts?${params.toString()}`,
      );
    },
  });

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Contracts (cross-tenant)</h1>
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="input max-w-[10rem]"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {STATUSES.map((s) => (
            <option key={s || 'all'} value={s}>
              {s || 'All statuses'}
            </option>
          ))}
        </select>
        <input
          className="input max-w-xs"
          placeholder="Filter by account ID"
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
        />
      </div>
      {isLoading ? <div>Loading…</div> : null}
      {error ? <div className="card text-red-600">{(error as Error).message}</div> : null}
      {data ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-2 py-1">Contract</th>
                <th className="px-2 py-1">Tenant</th>
                <th className="px-2 py-1">Status</th>
                <th className="px-2 py-1">Created</th>
                <th className="px-2 py-1">Sent</th>
                <th className="px-2 py-1">Signed</th>
              </tr>
            </thead>
            <tbody>
              {data.contracts.map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="px-2 py-1">
                    <Link className="text-brand hover:underline" to={`/contracts/${c.id}`}>
                      <code className="text-xs">{c.id.slice(0, 8)}</code>
                    </Link>
                  </td>
                  <td className="px-2 py-1">
                    <Link className="hover:underline" to={`/tenants/${c.account_id}`}>
                      {c.account_name ?? c.account_slug}
                    </Link>
                  </td>
                  <td className="px-2 py-1"><StatusBadge status={c.status} /></td>
                  <td className="px-2 py-1 text-slate-600">{formatDate(c.created_at)}</td>
                  <td className="px-2 py-1 text-slate-600">{formatDate(c.sent_at)}</td>
                  <td className="px-2 py-1 text-slate-600">{formatDate(c.signed_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
